import { CalendarOutlined, DownOutlined, UpOutlined } from '@ant-design/icons'
import {
  expensesCreateMutation,
  expensesListOptions,
  expensesListQueryKey,
  MonthlyExpensesReadable,
  sourcesListOptions,
  SourceTypeEnum
} from '@crm/api'
import { ReportSelect } from '@crm/features/reports/components'
import { useStyles } from '@crm/features/reports/styles'
import { defaultNonPagination, YEAR_FORMAT } from '@crm/utils'
import { TableContainer } from '@shared/components'
import { useInvalidatePredicateQueries, useQueryUpdate } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { format, showApiErrors, today } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Flex, notification, Select, Typography } from 'antd'
import { DefaultOptionType } from 'antd/lib/select'
import { useEffect, useMemo, useState } from 'react'

import { getMonthLabel, isEditableMonth, isoFirstDayOfMonth } from '../../utils'

import type { TableColumnsType } from 'antd'

const { Title, Text } = Typography

type MonthCell = { current?: number; budget?: number }
type RowWithMonths = MonthlyExpensesReadable & Record<string, MonthCell>

export const ReportExpenses = () => {
  const currentYear = parseInt(format(today, YEAR_FORMAT))

  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { t, currentLang } = useLocales()
  const { styles } = useStyles()
  const { params, updateQuery } = useQueryUpdate()

  const [options, setOptions] = useState<DefaultOptionType[]>([])
  const [yearOptions, setYearOptions] = useState({
    min: 10,
    max: 10
  })

  const monthISOs = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => isoFirstDayOfMonth(params.year, i)),
    [params.year]
  )

  useEffect(() => {
    setOptions([
      ...new Array(yearOptions.max).fill(null).map((_, i) => ({
        label: currentYear - i + yearOptions.max,
        value: currentYear - i + yearOptions.max
      })),
      { label: currentYear, value: currentYear },
      ...new Array(yearOptions.min)
        .fill(null)
        .map((_, i) => ({
          label: currentYear + i - yearOptions.min,
          value: currentYear + i - yearOptions.min
        }))
        .reverse()
    ])
  }, [yearOptions, currentYear])

  useEffect(() => {
    if (!params.year) {
      updateQuery({
        year: currentYear
      })
    }
  }, [params.year, currentYear, updateQuery])

  const { data, isLoading } = useQuery(expensesListOptions())

  const expensesByType = (data ?? {}) as Partial<
    Record<SourceTypeEnum, MonthlyExpensesReadable[]>
  >

  const { data: dataSources, isLoading: isLoadingSources } = useQuery({
    ...sourcesListOptions({ ...defaultNonPagination }),
    enabled: !!data,
    select: ({ results }) => {
      const items: Record<number, Record<string, unknown>> = {}

      results?.forEach(({ id, name, source_type }) => {
        if (id) {
          items[id] = { id: String(id), name, source_type: String(source_type) }

          if (!source_type) return

          const list = expensesByType?.[source_type]
          if (!list) return

          for (const { source, date, current, budget } of list) {
            if (source === id) {
              items[id][date] = { current, budget }
            }
          }
        }
      })

      return items
    }
  })

  const { mutate } = useMutation({
    ...expensesCreateMutation(),
    onSuccess: () => {
      invalidateQueries([expensesListQueryKey()])
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    },
    onError: (error) => showApiErrors(error)
  })

  const onChangeValue = (values: Partial<MonthlyExpensesReadable>) => {
    const { date = '', source = 0, current = 0, budget = 0 } = values
    mutate({ body: { date, source, current, budget } })
  }

  const getEditableConfig = (
    i: number,
    field: keyof MonthCell,
    date: string,
    source: number,
    expense?: MonthCell
  ) => {
    if (!isEditableMonth(i, params.year)) return false

    return {
      text: expense ? `${expense[field]}` : '',
      onChange: (value: string) => {
        const other: 'current' | 'budget' =
          field === 'current' ? 'budget' : 'current'

        onChangeValue({
          [field]: value,
          [other]: expense?.[other] ?? 0,
          date,
          source
        })
      }
    }
  }

  const columns = useMemo(() => {
    const year = Number(params?.year)

    const base: TableColumnsType<RowWithMonths> = [
      {
        title: t('nomenclature.lead_source'),
        children: [
          {
            title: t('nomenclature.online'),
            render: (row) => row?.name
          }
        ]
      }
    ]

    const monthCols = monthISOs.map((dateISO, i) => ({
      title: `${getMonthLabel(i, currentLang.value)} ${year}`,
      children: [
        {
          title: t('nomenclature.actual'),
          render: (row: RowWithMonths) => {
            const cell = row?.[dateISO]
            const value = cell?.current ?? 0

            if (row?.id === null) {
              return value
            }

            return (
              <Text
                className={styles.editCell}
                editable={
                  row?.id
                    ? getEditableConfig(i, 'current', dateISO, row?.id, cell)
                    : false
                }
              >
                {String(value)}
              </Text>
            )
          }
        },
        {
          title: t('nomenclature.budget'),
          render: (row: RowWithMonths) => {
            const cell = row?.[dateISO]
            const value = cell?.budget ?? 0

            if (row?.id === null) {
              return value
            }

            return (
              <Text
                className={styles.editCell}
                editable={
                  row?.id
                    ? getEditableConfig(i, 'budget', dateISO, row?.id, cell)
                    : false
                }
              >
                {String(value)}
              </Text>
            )
          }
        }
      ]
    }))

    return [...base, ...monthCols]
  }, [t, styles.editCell, currentLang.value, params.year])

  const dataTable = useMemo(() => {
    if (!dataSources) return []

    const expenses = Object.values(dataSources)

    const online = expenses?.filter(
      ({ source_type }) => source_type === SourceTypeEnum.ONLINE
    )
    const offline = expenses?.filter(
      ({ source_type }) => source_type === SourceTypeEnum.OFFLINE
    )
    const separatorRow: Record<string, unknown> = {
      id: null,
      name: <b>{t('nomenclature.offline')}</b>,
      source_type: SourceTypeEnum.OFFLINE
    }

    monthISOs.forEach((iso) => {
      separatorRow[iso] = {
        current: <b>{t('nomenclature.actual')}</b>,
        budget: <b>{t('nomenclature.budget')}</b>
      }
    })

    return [...online, separatorRow, ...offline]
  }, [dataSources, t, monthISOs])

  const filterYearOptions = options.map(({ value, label }) => ({
    label,
    value
  }))

  return (
    <Flex vertical gap={16}>
      <Flex gap={10}>
        <ReportSelect />

        <Select
          placeholder={t('filter.year')}
          value={params.year}
          onChange={(year) =>
            updateQuery({
              year: Number(year)
            })
          }
          suffixIcon={<CalendarOutlined />}
          filterOption={false}
          popupRender={(menu) => (
            <>
              <Button
                type="text"
                size="small"
                onClick={() =>
                  setYearOptions((prev) => ({ ...prev, max: prev.max + 10 }))
                }
                icon={<UpOutlined />}
                className="w-full"
              />

              {menu}

              <Button
                type="text"
                size="small"
                onClick={() =>
                  setYearOptions((prev) => ({ ...prev, min: prev.min + 10 }))
                }
                icon={<DownOutlined />}
                className="w-full"
              />
            </>
          )}
          options={filterYearOptions}
        />
      </Flex>

      <TableContainer
        rowKey={(row) => String(row.id ?? row.name)}
        title={() => <Title level={5}>{t('reports.report_expenses')}</Title>}
        data={{
          results: dataTable as RowWithMonths[],
          count: dataTable?.length
        }}
        columns={columns}
        loading={isLoading || isLoadingSources}
        showSearch={false}
        scroll={{ x: 1500 }}
        showSorterTooltip={false}
      />
    </Flex>
  )
}
